import { Injectable, OnDestroy } from '@angular/core';
import { BrowserDetectionService } from '@app/services/browser-detection.service';
import { NotificationsService } from '@app/services/notifications/notifications.service';
import { BrandingTemplate } from '@app/origami-api/constants';
import { getServerConfig } from '@app/origami-api/server-config';
import { StringHash } from '@app/shared/utils/utils-types';

export interface RequestParamOption {
  name: string;
  requestParam: string;
  value: string;
}

@Injectable()
export class PopupWindowService implements OnDestroy {
  private readonly strings = tiStrings.services.stringsPopupWindow;

  private windows: StringHash<Window> = {};

  constructor(private notificationsSevice: NotificationsService) {}

  ngOnDestroy(): void {
    this.windows = null;
  }

  openWindow(options: {
    url: string;
    name: string;
    entityId?: string;
    uniqWindow?: boolean;
    inNewTab?: boolean;
    additionalRequestParams?: RequestParamOption[];
    features?: string;
  }): void {
    const uniqViewerName = `${options.name}-${options.entityId}`;
    const currentViewerName = options.uniqWindow ? uniqViewerName : options.name;
    const prevViewerName = options.uniqWindow ? options.name : uniqViewerName;

    this.closeWindowByName(prevViewerName);

    this.windows[currentViewerName] = this.openPopup({
      url: options.url,
      name: currentViewerName,
      features: options.features || 'width=1000,height=800',
      inNewTab: options.inNewTab,
      additionalRequestParams: options.additionalRequestParams,
    });
  }

  closeWindow(name: string, entityId?: string): void {
    this.closeWindowByName(name);
    this.closeWindowByName(`${name}-${entityId}`);
  }

  openPopup(options: {
    url: string;
    name?: string;
    features?: string;
    noLocal?: boolean;
    inNewTab?: boolean;
    additionalRequestParams?: RequestParamOption[];
  }) {
    if (!options.url) {
      this.notificationsSevice.showWarning(this.strings.noAttachedFile);
      return null;
    }

    // (copied from old UI code)
    if (options.noLocal) {
      // to enable open window by domain
      let r = new RegExp('^https?://', 'i');
      if (!r.test(options.url)) {
        options.url = 'http://' + options.url;
      }
    }

    // (copied from old UI code)
    if (!options.inNewTab || BrowserDetectionService.isIE()) {
      if (!options.features) {
        // fix to make sure
        options.features = 'scrollbars=yes,resizable=yes'; // that new window will open with scrollbars enabled
      } else {
        // if caller didn't specify the opposite
        if (options.features.indexOf('scrollbars') === -1) {
          options.features += ',scrollbars=yes';
        }
        if (options.features.indexOf('resizable') === -1) {
          options.features += ',resizable=yes';
        }
      }
    }

    if (options.additionalRequestParams?.length) {
      options.additionalRequestParams.forEach((arp) => {
        const hasParams = options.url.indexOf('?') !== -1;
        const hasThisParam = options.url.indexOf(arp.requestParam) !== -1;
        const hasThisParamOn = options.url.indexOf(arp.requestParam + '=1') !== -1;
        const reqParamString = arp.requestParam + '=' + arp.value;
        if (!hasParams) {
          options.url = options.url + '?' + reqParamString;
        } else {
          if (!hasThisParam) {
            options.url = options.url + '&' + reqParamString;
          } else {
            //
          }
        }
      });
    }

    // Without the features argument, browsers open a new tab; with dimensions they open a popup window.
    const useTab = options.inNewTab && !BrowserDetectionService.isIE();
    const popup = useTab
      ? window.open(options.url, options.name || '_blank')
      : window.open(options.url, options.name, options.features);

    setTimeout(() => {
      if (this.hasPopupBlocker(popup)) {
        this.notificationsSevice.showWarning(
          this.strings.popupBlockerWarningMessage.replace(BrandingTemplate.companyName, getServerConfig().companyName),
          this.strings.popupBlockerWarningTitle,
        );
      }
    }, 1000);
  }

  private closeWindowByName(name: string) {
    const win = this.windows[name];

    if (win) {
      if (!win.closed) {
        win.close();
      }
      delete this.windows[name];
    }
  }

  // method copied from old code
  private hasPopupBlocker(poppedWindow: Window): boolean {
    let result = false;
    try {
      if (typeof poppedWindow === 'undefined') {
        result = true;
      } else if (poppedWindow && poppedWindow.closed) {
        result = false;
      } else if (poppedWindow) {
        if (BrowserDetectionService.isChrome()) {
          result = poppedWindow.outerHeight === 0 && poppedWindow.outerWidth === 0;
        } else {
          result = false;
        }
      } else {
        result = true;
      }
    } catch (err) {
      // do nothing
    }
    return result;
  }
}
